import { Button, DropdownSelect, Modal } from '@components/common';
import { DatePicker } from '@components/common/DatePicker';
import { FormInput, FormTextarea } from '@components/common/FormField';
import { useInventoryItemSelectOptions } from '@hooks/inventory';
import { Trash2 } from 'lucide-react';
import React, { useEffect, useId, useState } from 'react';
import type {
  InventoryStockLocationRecord,
  SaveStockAdjustmentRequest,
  StockAdjustmentRecord,
  StockAdjustmentType,
} from '@/renderer/types/inventory';

interface Props {
  adjustment: StockAdjustmentRecord | null;
  locations: InventoryStockLocationRecord[];
  companyId: number;
  outletId: string;
  isSaving: boolean;
  onSave: (req: SaveStockAdjustmentRequest) => void;
  onClose: () => void;
}

interface LineForm {
  key: string;
  inventoryItemId: string;
  quantityAdjusted: string;
  reason: string;
}

interface FormState {
  stockLocationId: string;
  adjustmentType: StockAdjustmentType;
  adjustmentDate: string;
  reference: string;
  notes: string;
  lines: LineForm[];
}

const ADJUSTMENT_TYPES: { value: StockAdjustmentType; label: string }[] = [
  { value: 'correction', label: 'Correction' },
  { value: 'wastage', label: 'Wastage / Spoilage' },
  { value: 'variance', label: 'Variance' },
  { value: 'stock_count', label: 'Stock Count' },
  { value: 'internal_use', label: 'Internal Use / Consumption' },
];

const makeKey = () => Math.random().toString(36).slice(2);

const emptyLine = (): LineForm => ({
  key: makeKey(),
  inventoryItemId: '',
  quantityAdjusted: '',
  reason: '',
});

const buildInitial = (
  adjustment: StockAdjustmentRecord | null,
  today: string,
): FormState => ({
  stockLocationId: adjustment?.stock_location_id ?? '',
  adjustmentType: adjustment?.adjustment_type ?? 'correction',
  adjustmentDate: adjustment?.adjustment_date ?? today,
  reference: adjustment?.reference ?? '',
  notes: adjustment?.notes ?? '',
  lines: adjustment?.lines?.map((l) => ({
    key: l.id,
    inventoryItemId: l.inventory_item_id,
    quantityAdjusted: String(l.quantity_adjusted),
    reason: l.reason ?? '',
  })) ?? [emptyLine()],
});

export const StockAdjustmentEditorModal: React.FC<Props> = ({
  adjustment,
  locations,
  companyId,
  outletId,
  isSaving,
  onSave,
  onClose,
}) => {
  const today = new Date().toISOString().split('T')[0];
  const uid = useId();
  const [form, setForm] = useState<FormState>(() =>
    buildInitial(adjustment, today),
  );

  const selectedItemIds = form.lines
    .map((l) => l.inventoryItemId)
    .filter(Boolean);
  const itemOptions = useInventoryItemSelectOptions({
    companyId,
    status: 'active',
    pageSize: 10,
    selectedItemIds,
  });

  useEffect(() => {
    setForm(buildInitial(adjustment, today));
    // today is stable within a render session — intentionally excluded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adjustment]);

  const setLine = (
    key: string,
    field: keyof Omit<LineForm, 'key'>,
    value: string,
  ) => {
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.map((l) =>
        l.key === key ? { ...l, [field]: value } : l,
      ),
    }));
  };

  const addLine = () =>
    setForm((prev) => ({ ...prev, lines: [...prev.lines, emptyLine()] }));

  const removeLine = (key: string) =>
    setForm((prev) => ({
      ...prev,
      lines: prev.lines.filter((l) => l.key !== key),
    }));

  const canSubmit =
    form.stockLocationId &&
    form.lines.some(
      (l) =>
        l.inventoryItemId &&
        l.quantityAdjusted !== '' &&
        Number(l.quantityAdjusted) !== 0,
    );

  const handleSubmit = () => {
    const validLines = form.lines.filter(
      (l) =>
        l.inventoryItemId &&
        l.quantityAdjusted !== '' &&
        Number(l.quantityAdjusted) !== 0,
    );
    onSave({
      id: adjustment?.id,
      companyId,
      outletId,
      stockLocationId: form.stockLocationId,
      adjustmentType: form.adjustmentType,
      adjustmentDate: form.adjustmentDate,
      reference: form.reference || null,
      notes: form.notes || null,
      lines: validLines.map((l) => ({
        inventoryItemId: l.inventoryItemId,
        quantityAdjusted: Number(l.quantityAdjusted),
        reason: l.reason || null,
      })),
    });
  };

  return (
    <Modal
      isOpen
      onClose={onClose}
      title={adjustment ? 'Edit Adjustment' : 'New Stock Adjustment'}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <DropdownSelect
            label="Location"
            value={form.stockLocationId}
            options={locations.map((l) => ({
              value: l.id,
              label: `${l.name} (${l.type})`,
            }))}
            placeholder="Select location"
            onChange={(value) =>
              setForm((prev) => ({ ...prev, stockLocationId: value }))
            }
          />
          <DropdownSelect
            label="Type"
            value={form.adjustmentType}
            options={ADJUSTMENT_TYPES}
            onChange={(value) =>
              setForm((prev) => ({
                ...prev,
                adjustmentType: value as StockAdjustmentType,
              }))
            }
          />
          <DatePicker
            mode="single"
            id={`${uid}-date`}
            label="Adjustment Date"
            value={form.adjustmentDate}
            onChange={(iso) =>
              setForm((prev) => ({ ...prev, adjustmentDate: iso }))
            }
          />
          <FormInput
            id={`${uid}-ref`}
            label="Reference"
            value={form.reference}
            onChange={(e) =>
              setForm((prev) => ({ ...prev, reference: e.target.value }))
            }
            placeholder="Optional"
          />
        </div>

        <FormTextarea
          id={`${uid}-notes`}
          label="Notes"
          value={form.notes}
          onChange={(e) =>
            setForm((prev) => ({ ...prev, notes: e.target.value }))
          }
          rows={2}
          placeholder="Optional notes"
        />

        <div>
          <div className="mb-2 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-black">Items</p>
              <p className="text-xs text-gray-500">
                Positive = add stock, negative = remove stock.
              </p>
            </div>
            <Button variant="text" size="sm" onClick={addLine}>
              + Add item
            </Button>
          </div>
          <div className="space-y-2">
            {form.lines.map((line) => (
              <div key={line.key} className="flex items-end gap-2">
                <div className="flex-1">
                  <DropdownSelect
                    label=""
                    value={line.inventoryItemId}
                    options={itemOptions.options}
                    placeholder="Select item"
                    searchable
                    searchPlaceholder="Search items"
                    onSearchChange={itemOptions.setSearchQuery}
                    hasMoreOptions={itemOptions.hasMore}
                    isLoadingOptions={itemOptions.isLoading}
                    onLoadMoreOptions={itemOptions.loadMore}
                    emptyStateLabel="No items found."
                    customDisplayLabel={itemOptions.getItemLabel(
                      line.inventoryItemId,
                    )}
                    onChange={(value) =>
                      setLine(line.key, 'inventoryItemId', value)
                    }
                    menuPlacement="up"
                  />
                </div>
                <div className="w-28">
                  <FormInput
                    id={`${uid}-qty-${line.key}`}
                    label=""
                    type="number"
                    step="0.0001"
                    value={line.quantityAdjusted}
                    onChange={(e) =>
                      setLine(line.key, 'quantityAdjusted', e.target.value)
                    }
                    placeholder="Qty (±)"
                  />
                </div>
                <div className="flex-1">
                  <FormInput
                    id={`${uid}-reason-${line.key}`}
                    label=""
                    value={line.reason}
                    onChange={(e) =>
                      setLine(line.key, 'reason', e.target.value)
                    }
                    placeholder="Reason (optional)"
                  />
                </div>
                {form.lines.length > 1 && (
                  <button
                    type="button"
                    onClick={() => removeLine(line.key)}
                    className="mb-1 rounded-lg p-2 text-gray-400 hover:bg-gray-100 hover:text-red-500"
                  >
                    <Trash2 size={16} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <Button variant="text" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || isSaving}
            isLoading={isSaving}
          >
            {adjustment ? 'Save Changes' : 'Create Adjustment'}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
